/**
 * TicketType Entity Schemas
 *
 * A TicketType is a priced/capacitated tier on a TicketedEvent. Doubles as a
 * volunteer-signup slot when the parent event has kind=signup (priceCents
 * stays null; slotStartsAt/slotEndsAt narrow the time window).
 */

import { z } from 'zod'
import { SlotStateSchema } from './signup-sheet-rule'

export const TicketTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  capacity: z.number().int(),
  capacityRemaining: z.number().int(),
  perOrderMax: z.number().int(),
  priceCents: z.number().int().nullable(),
  sortOrder: z.number().int(),
  isHidden: z.boolean(),
  slotStartsAt: z.string().nullable(),
  slotEndsAt: z.string().nullable(),
  groupLabel: z.string().nullable(),
  // Recurring signup sheets (plan 2026-09-18-0147, U1 / KTD2). All optional for
  // the same reason `TicketTypePublicSchema.capacity` is — see the rationale
  // there; a cached admin payload written before the backend deploy must keep
  // parsing.
  //
  // `generatedKey` is the slot's local date and start time (`20261126T1515`),
  // unique per sheet, and is what makes minting idempotent. Null on a slot an
  // organizer typed by hand.
  generatedKey: z.string().nullable().optional(),
  // The materialized cutoff this slot closes at, derived from the sheet's close
  // rule. Null when the sheet has no close rule.
  cutoffAt: z.string().nullable().optional(),
  // Set the moment an organizer edits this slot individually, which exempts it
  // from later rule propagation (R4).
  ruleOverriddenAt: z.string().nullable().optional(),
  // Slot cancellation (R19). A cancelled slot keeps its key so the mint never
  // resurrects it.
  cancelledAt: z.string().nullable().optional(),
  cancelReason: z.string().nullable().optional(),
  // Derived at read time from the columns above plus `capacityRemaining`; never
  // stored (KTD2). Nullable as well as optional: the mapper emits an explicit
  // null on every event that is not a recurring sheet, which is every event in
  // production today.
  slotState: SlotStateSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string().nullable()
})
export type TicketType = z.infer<typeof TicketTypeSchema>

export const TicketTypePublicSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  // The row's total, so a public surface can render `remaining of total`
  // instead of printing the remainder on both sides of a fraction.
  //
  // Optional, and deliberately so: the mobile app parses this payload with Zod
  // at runtime and ships on its own cadence, so a build that reaches users
  // before the backend deploy would hard-fail the parse on a required field and
  // take the whole events screen down. Renderers fall back to a bare remainder
  // when it is absent — never to `remaining of remaining`, which was the bug.
  // The backend always populates it; see PublicTicketTypeDto, where it is
  // required.
  capacity: z.number().int().optional(),
  capacityRemaining: z.number().int(),
  perOrderMax: z.number().int(),
  priceCents: z.number().int().nullable(),
  sortOrder: z.number().int(),
  slotStartsAt: z.string().nullable(),
  slotEndsAt: z.string().nullable(),
  groupLabel: z.string().nullable(),
  // Recurring signup sheets (plan 2026-09-18-0147, U1 / KTD2, KTD8). Optional
  // for exactly the reason `capacity` above is: the mobile app parses this
  // payload strictly and ships on its own cadence, so a required new field
  // would hard-fail the parse on any build that reaches users before the
  // backend deploy. Renderers fall back to today's wording when `slotState` is
  // absent. The backend always populates all three on a recurring sheet.
  //
  // The organizer-only columns (`ruleOverriddenAt`, `cancelledAt`,
  // `cancelReason`) deliberately stay off this schema — a cancelled slot never
  // reaches a registrant surface at all (R19), so its reason is not public.
  generatedKey: z.string().nullable().optional(),
  cutoffAt: z.string().nullable().optional(),
  // Nullable as well as optional, matching `cutoffAt` and `generatedKey` above:
  // the mapper emits an explicit null on every event that is not a recurring
  // sheet, and zod rejects null on a bare `.optional()`.
  slotState: SlotStateSchema.nullable().optional()
})
export type TicketTypePublic = z.infer<typeof TicketTypePublicSchema>
